import { TagTypes } from "../../../Api/Types/tagTypes";
import { cleanParams } from "../../Shared/Helpers/cleanParams";
import { PagedList } from "../../Shared/Types/common";
import {
  Reservation,
  ReservationFiltersDto,
  ReservationPartyFindOrCreateRequest,
  ReservationListRequest,
  ReservationPartyLookupDto,
  ReservationPartyLookupRequest,
  ReservationPricePreviewRequest,
  ReservationPricePreviewResult,
} from '../Types/reservationTypes';
import { buildReservationPartiesLookupUrl } from './reservationPartiesLookupQuery';
import { reservationsApi } from './reservationsApi';

const extendedReservationsApi = reservationsApi.injectEndpoints({
  // Ensure this is set to true to override existing endpoints
  endpoints: (builder) => ({
    getReservationsList: builder.query<PagedList<Reservation>, ReservationListRequest>({
      query: (params) => ({
        url: '/reservations/search',
        params: cleanParams(params),
      }),
      providesTags: [{ type: TagTypes.Reservation, id: 'List' }],
    }),
    getReservationsFilterSelects: builder.query<ReservationFiltersDto, void>({
      query: () => '/reservations/filters',
      providesTags: [{ type: TagTypes.Reservation, id: 'List' }],
    }),
    getReservationWithAssociationsById: builder.query<Reservation, number>({
      query: (id) => `/reservations/with-associations/${id}`,
      providesTags: (result, error, id) => [
        { type: TagTypes.Reservation, id },
        // Include CashDocument tag for auto-created KP
        { type: TagTypes.CashDocument, id: 'List' },
      ],
    }),
    getReservationPartiesLookup: builder.query<
      ReservationPartyLookupDto[],
      ReservationPartyLookupRequest | void
    >({
      query: (params) => buildReservationPartiesLookupUrl(params),
      providesTags: [{ type: TagTypes.Reservation, id: 'List' }],
    }),
    getReservationPricePreview: builder.mutation<
      ReservationPricePreviewResult,
      ReservationPricePreviewRequest
    >({
      query: (body) => ({
        url: '/reservations/preview',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: TagTypes.Reservation, id: 'List' }],
    }),
    findOrCreateReservationParty: builder.mutation<
      ReservationPartyLookupDto,
      ReservationPartyFindOrCreateRequest
    >({
      query: (body) => ({
        url: '/reservations/parties/find-or-create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: TagTypes.Reservation, id: 'List' }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetReservationsListQuery,
  useGetReservationsFilterSelectsQuery,
  useGetReservationWithAssociationsByIdQuery,
  useGetReservationPartiesLookupQuery,
  useGetReservationPricePreviewMutation,
  useFindOrCreateReservationPartyMutation,
} = extendedReservationsApi;
